import {ReactElement, useRef} from 'react';

import {Alert, Stack,} from '@mantine/core';
import {useNavigate} from 'react-router-dom';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableColumn} from "mantine-datatable";

import {useExecutions} from '../hooks/useExecutions.ts';
import {useExecutionsFilter} from '../hooks/useExecutionsFilter.ts';
import {useExecutionsSearch} from '../hooks/useExecutionsSearch.ts';
import {Execution} from '../types.ts';

export const ExecutionsTable = ({
                                    extraTools: _extraTools,
                                    settings: _settings,
                                }: {
    extraTools?: ReactElement[],
    settings?: { label: string, onClick: () => void }[]
} = {}) => {

    const navigate = useNavigate();

    const {filters} = useExecutionsFilter();

    const {
        // The full loaded set BEFORE keyword filtering — the window the
        // footer's match count is measured against.
        items,
        visibleItems: records,
        columns,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchNextPage,
    } = useExecutions(filters);

    // Same module-scoped store the list hook filters on — this component owns
    // the input, useExecutions owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = useExecutionsSearch();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Two distinct empty states that mean opposite things. Collapsing them into
    // one message sends an operator who mistyped a keyword hunting for a broken
    // console when nothing is wrong.
    const noRecordsText = keyword
        ? `No executions match "${keyword}" in the records loaded so far.`
        : 'No executions have been started yet.';

    return (
        <Stack>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title='Could not load executions'>
                    The workflows API did not return the list. Try Reload; if it persists the
                    API may be unavailable in this environment.
                </Alert>
            )}

            <MantineDataTable
                columns={columns as DataTableColumn[]}
                records={records}
                // Sorting is client-side over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchNextPage()}
                scrollViewportRef={scrollViewportRef}
                onRowClick={({record}) =>
                    navigate(`/executions/${encodeURIComponent((record as Execution).id)}`)}
            />

            <DataTableTokenPagination
                loadedCount={records.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                isFetchingMore={isFetchingNextPage}
                nextToken={nextToken}
                handleLoadMore={fetchNextPage}
                noun='execution'
            />
        </Stack>
    );
};
